import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import type { UserRole, UserStatus } from '@xdocs/schemas';
import { Avatar, Badge, EmptyState, Skeleton, Tabs, useI18n, cn } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { formatDate } from '@xdocs/i18n';
import { RoleGate, useAdminT, useAdminUserDetail } from '../../../features/admin/index.js';
import { ActivityTab } from './tabs/ActivityTab.js';
import { CreditsTab } from './tabs/CreditsTab.js';
import { ProfileTab } from './tabs/ProfileTab.js';
import { SessionsTab } from './tabs/SessionsTab.js';
import { SubscriptionTab } from './tabs/SubscriptionTab.js';
import styles from './UserDetailPage.module.css';

const ROLE_TONE: Record<UserRole, BadgeTone> = { admin: 'error', moderator: 'warning', user: 'neutral' };
const STATUS_TONE: Record<UserStatus, BadgeTone> = { active: 'success', suspended: 'error', deleted: 'neutral' };

type Tab = 'profile' | 'credits' | 'sessions' | 'subscription' | 'activity';

/** `/admin/users/:userId` (F54 detail screen, design/app/40-admin-user-detail.html). */
export function UserDetailPage(): ReactElement {
  return (
    <RoleGate minimum="admin">
      <UserDetailContent />
    </RoleGate>
  );
}

function UserDetailContent(): ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const { locale, t: tCommon } = useI18n();
  const { t } = useAdminT();
  const navigate = useNavigate();
  const { detail, isLoading, isError, error, refetch } = useAdminUserDetail(userId ?? '');
  const [tab, setTab] = useState<Tab>('profile');

  if (isError) {
    return <EmptyState kind="error" title={t('admin:userDetail.error.title')} description={error?.message} action={{ label: tCommon('common:action.retry'), onClick: refetch }} />;
  }

  if (isLoading || !detail) {
    return (
      <div className={cn(styles['loading'])} aria-busy="true">
        <Skeleton shape="tile" height="80px" />
        <Skeleton shape="row" count={4} />
      </div>
    );
  }

  const { user } = detail;

  return (
    <div className={cn(styles['page'])}>
      <button type="button" className={cn(styles['back'])} onClick={() => void navigate('/admin/users')}>
        <ArrowLeft size={14} aria-hidden="true" />
        {t('admin:userDetail.back')}
      </button>

      <div className={cn(styles['header'])}>
        <Avatar name={user.displayName ?? user.email} size={64} />
        <div>
          <div className={cn(styles['nameRow'])}>
            <h1 className={cn(styles['name'])}>{user.displayName ?? user.email}</h1>
            <Badge tone={ROLE_TONE[user.role]}>{t(`admin:role.${user.role}`)}</Badge>
            <Badge tone={STATUS_TONE[user.status]}>{t(`admin:status.${user.status}`)}</Badge>
          </div>
          <div className={cn(styles['meta'])}>{t('admin:userDetail.meta', { email: user.email, date: formatDate(locale, user.createdAt, { dateStyle: 'medium' }) })}</div>
        </div>
      </div>

      <div className={cn(styles['liveNotice'])}>{t('admin:userDetail.liveNotice')}</div>

      <Tabs<Tab>
        label={user.displayName ?? user.email}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'profile', label: t('admin:userDetail.tabs.profile') },
          { value: 'credits', label: t('admin:userDetail.tabs.credits') },
          { value: 'sessions', label: t('admin:userDetail.tabs.sessions') },
          { value: 'subscription', label: t('admin:userDetail.tabs.subscription') },
          { value: 'activity', label: t('admin:userDetail.tabs.activity') },
        ]}
      />

      <div className={cn(styles['tabPanel'])}>
        {tab === 'profile' && <ProfileTab detail={detail} />}
        {tab === 'credits' && <CreditsTab detail={detail} />}
        {tab === 'sessions' && <SessionsTab detail={detail} />}
        {tab === 'subscription' && <SubscriptionTab detail={detail} />}
        {tab === 'activity' && <ActivityTab userId={user.id} />}
      </div>
    </div>
  );
}
